// 特殊演出スケジューラ — スピン開始時に winner の role を受け取り、
// 演出を timing × totalMs 後に setTimeout で発火する。
//
// 抽選ロジック (現在は全演出から1つだけ):
//   1) enabled + role 一致 + probability(範囲抽選) で発火する候補を全列挙
//   2) 候補から1つだけランダム抽選
//   3) variant を selectedVariant (0=ランダム, 1-5=固定) で決定
//   4) timing を timingRange からランダム抽選

import type { EffectKey, RangeValue, SpecialRole, SpinEffectSettings } from './types';

/** どの role のときに各 effect が発火するか */
const EFFECT_TRIGGERS: Record<EffectKey, readonly SpecialRole[]> = {
  soundConfirm:    ['target'],
  soundExpect:     ['target'],
  soundNgConfirm:  ['avoid'],
  miniCharTarget:  ['target'],
  miniCharExpect:  ['target'],
  miniCharNg:      ['avoid'],
  cutInTarget:     ['target'],
  cutInExpect:     ['target'],
  cutInNg:         ['avoid'],
  flashConfirm:    ['target'],
  wheelGlow:       ['target', 'avoid'],
  textChance:      ['target'],
};

/** scheduler は variant を受けるためコールバック生成関数を渡す方式 */
export type EffectCallbackFactory = (variant: number) => Partial<Record<EffectKey, () => void>>;
export type EffectCallbacks = Partial<Record<EffectKey, () => void>>;

/** RangeValue から uniform random で実効値を取得 */
function pickRange(range: RangeValue | undefined, fallback: number): number {
  if (!range) return fallback;
  const min = Math.max(0, Math.min(1, range.min));
  const max = Math.max(min, Math.min(1, range.max));
  return min + Math.random() * (max - min);
}

/** selectedVariant が 0 ならランダム (1〜5)、1〜5 なら固定 */
function pickVariant(selectedVariant: number | undefined): number {
  if (selectedVariant && selectedVariant >= 1 && selectedVariant <= 5) {
    return selectedVariant;
  }
  return 1 + Math.floor(Math.random() * 5);
}

export class EffectScheduler {
  private timers: ReturnType<typeof setTimeout>[] = [];

  schedule(
    winnerRole: SpecialRole | undefined,
    settings: SpinEffectSettings,
    totalMs: number,
    callbackFactory: EffectCallbackFactory,
  ): void {
    this.cancel();
    if (!settings.enabled || !winnerRole) return;

    // 1) 候補列挙: enabled + role 一致 + probability 抽選通過
    //    cfg.probabilityRandom = true なら 0〜1 完全ランダム、false なら probabilityRange から抽選
    const candidates: EffectKey[] = [];
    for (const key of Object.keys(settings.effects) as EffectKey[]) {
      const cfg = settings.effects[key];
      if (!cfg || !cfg.enabled) continue;
      if (!EFFECT_TRIGGERS[key].includes(winnerRole)) continue;
      const probability = cfg.probabilityRandom ? Math.random() : pickRange(cfg.probabilityRange, 0.20);
      if (Math.random() > probability) continue;
      candidates.push(key);
    }
    if (candidates.length === 0) return;

    // 2) 全候補から1つだけランダム抽選
    const chosen = candidates[Math.floor(Math.random() * candidates.length)];
    const cfg = settings.effects[chosen];
    if (!cfg) return;
    // 3) variant 抽選 (selectedVariant が 0 ならランダム、1-5 なら固定)
    const variant = pickVariant(cfg.selectedVariant);
    // 4) timing 抽選: timingRandom なら完全ランダム、false なら範囲から
    const timing = cfg.timingRandom ? Math.random() : pickRange(cfg.timingRange, 0.50);
    const cb = callbackFactory(variant)[chosen];
    if (!cb) return;
    const delayMs = Math.max(0, totalMs * timing);
    this.timers.push(setTimeout(cb, delayMs));
  }

  cancel(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }
}
